import React from 'react';

export const AdidasLogo = ({ className = "w-10 h-10" }: { className?: string }) => (
    <svg viewBox="-3 10 36 21" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 30H23.6L12.4 10.8L20.8 10.8L32 30Z" />
        <path d="M21.4 30H13L5 16.3H13.4L21.4 30Z" />
        <path d="M10.8 30H2.4L-2.4 21.7H6L10.8 30Z" />
    </svg>
);

interface KPICardProps {
    title: string;
    value: string | number;
    color: string;
    icon: React.ComponentType<any>;
    subtext?: string;
}

export const KPICard: React.FC<KPICardProps> = ({ title, value, color, icon: Icon, subtext }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-start justify-between hover:shadow-md transition-shadow">
        <div>
            <p className="text-xs text-gray-500 font-bold uppercase mb-1">{title}</p>
            <h3 className="text-2xl font-bold">{value}</h3>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
            <Icon size={20} className={color.replace('bg-', 'text-')} />
        </div>
    </div>
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'danger' | 'success';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    children,
    className = '',
    ...props
}) => {
    const baseClasses = 'font-bold rounded-lg transition-all flex items-center justify-center';

    const variantClasses = {
        primary: 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg',
        secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300',
        danger: 'bg-red-600 text-white hover:bg-red-700 shadow-md',
        success: 'bg-green-600 text-white hover:bg-green-700 shadow-md shadow-green-200',
    };

    const sizeClasses = {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
    };

    return (
        <button
            className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
};

interface BadgeProps {
    children: React.ReactNode;
    variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
    className?: string;
}

export const Badge: React.FC<BadgeProps> = ({ children, variant = 'default', className = '' }) => {
    const variantClasses = {
        default: 'bg-gray-100 text-gray-800',
        primary: 'bg-blue-100 text-blue-800',
        success: 'bg-green-100 text-green-800',
        warning: 'bg-yellow-100 text-yellow-800',
        danger: 'bg-red-100 text-red-800',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs font-bold ${variantClasses[variant]} ${className}`}>
            {children}
        </span>
    );
};

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    maxWidth = 'md'
}) => {
    if (!isOpen) return null;

    const maxWidthClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in">
            <div className={`bg-white rounded-xl w-full ${maxWidthClasses[maxWidth]} shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4`}>
                <div className="bg-gray-100 px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="font-bold text-gray-900">{title}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-black transition"
                        aria-label="Cerrar"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

interface LoadingSpinnerProps {
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', className = '' }) => {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-8 h-8',
        lg: 'w-12 h-12',
    };

    return (
        <div className={`${sizeClasses[size]} ${className}`}>
            <svg className="animate-spin text-black" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
        </div>
    );
};

interface EmptyStateProps {
    icon?: React.ComponentType<any>;
    title: string;
    description?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
}

export const EmptyState: React.FC<EmptyStateProps> = ({ icon: Icon, title, description, action }) => (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        {Icon && (
            <div className="mb-4 text-gray-300">
                <Icon size={64} />
            </div>
        )}
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        {description && <p className="text-sm text-gray-500 mb-6 max-w-md">{description}</p>}
        {action && (
            <Button onClick={action.onClick} variant="primary">
                {action.label}
            </Button>
        )}
    </div>
);
